import DatePicker from "components/DatePicker/DatePicker";
import SelectDropDown from "components/SelectDropDown/SelectDropDown";
import React, { useState, useMemo, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTable } from "react-table";
import "./logs.scss";
import Button from "components/Button/index";
import useScreenMobile from "hooks/useScreen";
import noData from "assets/images/homepage/PasswordManger/noResult/Nodata.png";
import MenuDropDown from "components/DropDowns/MenuDropDown/index";
import ListIcons from "constants/Homepage/ysafe/menuList/listIconsFunc";
import { useToast } from "components/Toaster/index";
import useTheme from "hooks/observer";
import { getLogFiles, download_log_file as downloadLogFile, get_inclusion_proof as getInclusionProof } from "@induz/sdk";
import { generateProofPDF } from "./GenerateProofPDF";


const Logs = ({ organization }) => {
  const isMobile = useScreenMobile({ size: 992 });
  const showToast = useToast();
  const theme = useTheme();

  const [selectedSecretType, setSelectedSecretType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [logData, setLogData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState({
    selectedRow: null,
    selectedOption: ""
  });
  const menuRef = useRef(null);
  const processedActionRef = useRef(null);
  const [subDropDown, setSubDropDown] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // const secretTypeOptions = [
  //   "File Upload",
  //   "File Sync",
  //   "File Backup",
  //   "Delete File",
  //   "Share File"
  // ];

  // const users = [
  //   "Rohan Sharma",
  //   "Rajesh Gupta",
  //   "Lakshmi Iyer",
  //   "Anjali Rao",
  //   "Aditi Mehta",
  //   "Arjunan",
  //   "Santhosh",
  //   "Aravind"
  // ];

  const logMenuList = [
    { icon: <ListIcons type="download" />, text: "Download this file" },
    { icon: <ListIcons type="proof" />, text: "Get proof" },
    { icon: <ListIcons type="details" />, text: "Details" }
  ];

  // Fetch log files from API
  const fetchLogFiles = useCallback(async (startTime, endTime) => {
    setLoading(true);
    try {
      // Backend takes time in microseconds
      const response = await getLogFiles(startTime * 1e6, endTime * 1e6);
      setLogData(response || []);
    } catch (error) {
      console.error("Error fetching log files:", error);
      showToast("Failed to fetch log files", "error");
      setLogData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Convert microseconds to readable date format
  const formatMicrosecondsToDate = useCallback((microseconds) => {
    if (!microseconds) return "N/A";
    const date = new Date(microseconds / 1000); // Convert microseconds to milliseconds
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  }, []);

  // Format file size to readable format
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + " " + sizes[i];
  }, []);

  // Fetch logs on component mount (last 7 days)
  useEffect(() => {
    const now = Math.floor(Date.now() / 1000); // Current time in UTC seconds
    const sevenDaysAgo = now - (7 * 24 * 60 * 60); // 7 days ago in UTC seconds
    console.log("(mounted)Fetching logs for last 7 days:", sevenDaysAgo, now);
    fetchLogFiles(sevenDaysAgo, now);
  }, []); // Only run once on mount

  // Fetch logs when date range changes
  // useEffect(() => {
  //   if (selectedDate) {
  //     const { start, end } = parseSelectedDateRange(selectedDate);
  //     if (start && end) {
  //       const startTime = Math.floor(start.getTime() / 1000); // Convert to UTC seconds
  //       const endTime = Math.floor(end.getTime() / 1000); // Convert to UTC seconds
  //       fetchLogFiles(startTime, endTime);
  //     }
  //   }
  // }, [selectedDate]); // Only depend on selectedDate
  // Fetch logs when date range changes
  useEffect(() => {
    if (selectedDate) {
      const { start, end } = parseSelectedDateRange(selectedDate);
      if (start && end) {
        // Check if date range exceeds 365 days
        const daysDifference = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDifference > 365) {
          showToast("Date range cannot exceed 365 days", "error");
          return;
        }

        // Set start time to beginning of day (00:00:00)
        start.setHours(0, 0, 0, 0);

        // Set end time to end of day (23:59:59)
        end.setHours(23, 59, 59, 999);

        const startTime = Math.floor(start.getTime() / 1000); // Convert milliseconds to UTC seconds
        const endTime = Math.floor(end.getTime() / 1000); // Convert milliseconds to UTC seconds
        console.log("end time:", endTime);

        console.log("Fetching logs from:", start.toLocaleString(), "to", end.toLocaleString());
        fetchLogFiles(startTime, endTime);
      }
    }
  }, [selectedDate]); // Only depend on selectedDate

  const handleDownloadLog = useCallback(async (logFileName) => {
    try {
      console.log("Downloading log file:", logFileName);
      const response = await downloadLogFile(logFileName);
      console.log("Response:", response);
      const bytesData = response.data;
      // Calculate SHA-256 hash of the downloaded bytes and print it
      if (bytesData && bytesData instanceof Uint8Array) {
        const hashBuffer = await window.crypto.subtle.digest("SHA-256", bytesData);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
        console.log("SHA-256 of downloaded data:", hashHex);

        // Create a blob and download the file
        const blob = new Blob([bytesData], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${logFileName}.bin`;
        link.click();

        // Clean up the URL object
        URL.revokeObjectURL(link.href);

        showToast("Log file downloaded successfully", "success");
      } else {
        console.log("Downloaded data is not a Uint8Array");
        showToast("Invalid log file data", "error");
      }
    } catch (error) {
      console.error("Error downloading log file:", error);
      showToast("Failed to download log file", "error");
    } finally {
      setMenu({ selectedRow: null, selectedOption: "" });
    }
  }, [showToast]);

  // Merkle Tree Inclusion Proof Verification Function
  const verifyInclusion = useCallback((leafHash, rootHash, leafIndex, leafCount, inclusionPath) => {
    // Step 1: If leaf_index >= leaf_count then fail verification
    if (!(leafIndex < leafCount)) {
      console.log("Verification failed: invalid inputs - leaf_index >= leaf_count");
      return false;
    }

    // Step 2: Initialize fn and sn
    let fnVal = leafIndex;
    let snVal = leafCount - 1;

    // Step 3: Set r = leaf_hash
    let r = new Uint8Array(leafHash);

    // Helper function to compute SHA-256 hash of concatenated arrays
    const nodeHash = async (left, right) => {
      const combined = new Uint8Array(left.length + right.length);
      combined.set(left, 0);
      combined.set(right, left.length);
      const hashBuffer = await window.crypto.subtle.digest("SHA-256", combined);
      return new Uint8Array(hashBuffer);
    };

    // Step 4: Iterate through each p in inclusion_path array
    const verifyAsync = async () => {
      for (const p of inclusionPath) {
        if (snVal === 0) {
          console.log("Verification failed in loop - sn_val is 0");
          return false;
        }

        if ((fnVal & 1) === 1 || fnVal === snVal) {
          // Step 4.b.i: If LSB(fn) set OR fn == sn
          r = await nodeHash(p, r);

          // If LSB(fn) not set, right-shift both until LSB(fn) is set or fn == 0
          if ((fnVal & 1) === 0) {
            while (!((fnVal & 1) === 1 || fnVal === 0)) {
              fnVal >>= 1;
              snVal >>= 1;
            }
          }
        } else {
          // Otherwise
          r = await nodeHash(r, p);
        }

        // Finally, right-shift both fn and sn one time
        fnVal >>= 1;
        snVal >>= 1;
      }

      // Step 5: Verify r == root_hash && sn == 0
      const okSn = snVal === 0;
      const okR = r.length === rootHash.length && r.every((val, idx) => val === rootHash[idx]);

      console.log("Verification result - okSn:", okSn, "okR:", okR);
      return okSn && okR;
    };

    return verifyAsync();
  }, []);

  const handleGetProof = useCallback(async (logFileName) => {
    try {
      console.log("Getting proof for log file:", logFileName);
      const inclusionProof = await getInclusionProof(logFileName);
      console.log("Inclusion proof response:", inclusionProof);


      // Verify the inclusion proof
      const isVerified = await verifyInclusion(
        inclusionProof.leaf_hash,
        inclusionProof.root_hash,
        inclusionProof.leaf_index,
        inclusionProof.leaf_count,
        inclusionProof.proof
      );

      console.log("Proof verification result:", isVerified);

      // Helper function to convert Uint8Array to hex string
      const uint8ArrayToHex = (uint8Array) => {
        if (!uint8Array) return "N/A";
        return Array.from(uint8Array)
          .map(byte => byte.toString(16).padStart(2, "0"))
          .join("");
      };

      // Transform InclusionProof data to PDF format
      const proofData = {
        fileName: logFileName,
        leafHash: uint8ArrayToHex(inclusionProof.leaf_hash),
        rootHash: uint8ArrayToHex(inclusionProof.root_hash),
        leafIndex: inclusionProof.leaf_index,
        leafCount: inclusionProof.leaf_count,
        inclusionPath: inclusionProof.proof.map(uint8ArrayToHex),
        verified: isVerified, // Use actual verification result
        timestamp: new Date().toLocaleString(),
        transactionId: inclusionProof.transaction_id
      };

      // Generate and download PDF
      generateProofPDF(proofData);

      if (isVerified) {
        showToast("Proof verified and PDF generated successfully", "success");
      } else {
        showToast("Proof verification failed - PDF generated with failure status", "warning");
      }

    } catch (error) {
      console.error("Error getting proof:", error);
      showToast("Failed to get inclusion proof", "error");
    } finally {
      setMenu({ selectedRow: null, selectedOption: "" });
    }
  }, [showToast, verifyInclusion]);

  // Handle menu close
  const handleMenu = () => {
    setMenu({ selectedRow: null, selectedOption: "" });
  };

  // Handle menu selections with ref to prevent infinite loop
  useEffect(() => {
    const actionKey = `${menu.selectedOption}-${selectedRow?.file_name}`;

    if (menu.selectedOption && selectedRow && menu.selectedOption !== "menu" && processedActionRef.current !== actionKey) {
      processedActionRef.current = actionKey;

      if (menu.selectedOption === "Download this file") {
        handleDownloadLog(selectedRow.file_name);
      } else if (menu.selectedOption === "Get proof") {
        handleGetProof(selectedRow.file_name);
      }
      // Details is handled separately via conditional render, no need to process here
    }

    // Reset ref when menu is closed
    if (!menu.selectedOption) {
      processedActionRef.current = null;
    }
  }, [menu.selectedOption, selectedRow, handleDownloadLog, handleGetProof]);

  const calculateMenuPosition = useCallback((triggerRect) => {
    const MENU_WIDTH = 220;
    const MENU_HEIGHT = 150;
    const OFFSET = 8;
    const VIEWPORT_PADDING = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceRight = viewportWidth - triggerRect.right;

    const top = spaceBelow >= MENU_HEIGHT + OFFSET
      ? triggerRect.bottom + OFFSET
      : Math.max(VIEWPORT_PADDING, triggerRect.top - MENU_HEIGHT - OFFSET);

    const left = spaceRight >= MENU_WIDTH
      ? triggerRect.right - MENU_WIDTH
      : Math.max(
          VIEWPORT_PADDING,
          Math.min(triggerRect.left, viewportWidth - MENU_WIDTH - VIEWPORT_PADDING)
        );

    return { top, left };
  }, []);

  useEffect(() => {
    if (!menu.selectedRow || !menuRef.current) return undefined;

    const updateMenuPosition = () => {
      if (!menuRef.current) return;
      const triggerRect = menuRef.current.getBoundingClientRect();
      setMenuPosition(calculateMenuPosition(triggerRect));
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [menu.selectedRow, calculateMenuPosition]);

  const columns = useMemo(
    () => [
      {
        Header: "Log File Name",
        accessor: "file_name",
        width: isMobile ? "35%" : "35%",
        Cell: ({ value }) => (
          <div className="log-file-name">{value || "N/A"}</div>
        )
      },
      {
        Header: "Start Time",
        accessor: "start_time",
        width: isMobile ? "22%" : "22.5%",
        Cell: ({ value }) => (
          <div className="log-time">{formatMicrosecondsToDate(value)}</div>
        )
      },
      {
        Header: "End Time",
        accessor: "end_time",
        width: isMobile ? "22%" : "22.5%",
        Cell: ({ value }) => (
          <div className="log-time">{formatMicrosecondsToDate(value)}</div>
        )
      },
      !isMobile && {
        Header: "Size",
        accessor: "file_size",
        width: "10%",
        Cell: ({ value }) => (
          <div className="log-size">{formatFileSize(value)}</div>
        )
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "10%",
        Cell: ({ row }) => (
          <div
            className="actionIconBox"
            onClick={(e) => {
              if (!e || !e.currentTarget) return;

              e.stopPropagation();
              menuRef.current = e.currentTarget;
              setMenuPosition(calculateMenuPosition(e.currentTarget.getBoundingClientRect()));
              setSelectedRow(row.original);
              setMenu({
                selectedRow: row.original.file_name,
                selectedOption: "menu"
              });
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.66406 9.99967C9.66406 10.2207 9.75186 10.4326 9.90814 10.5889C10.0644 10.7452 10.2764 10.833 10.4974 10.833C10.7184 10.833 10.9304 10.7452 11.0867 10.5889C11.2429 10.4326 11.3307 10.2207 11.3307 9.99967C11.3307 9.77866 11.2429 9.5667 11.0867 9.41042C10.9304 9.25414 10.7184 9.16634 10.4974 9.16634C10.2764 9.16634 10.0644 9.25414 9.90814 9.41042C9.75186 9.5667 9.66406 9.77866 9.66406 9.99967Z"
                stroke="var(--text-grey-neutral)"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.66406 15.833C9.66406 16.054 9.75186 16.266 9.90814 16.4223C10.0644 16.5785 10.2764 16.6663 10.4974 16.6663C10.7184 16.6663 10.9304 16.5785 11.0867 16.4223C11.2429 16.266 11.3307 16.054 11.3307 15.833C11.3307 15.612 11.2429 15.4 11.0867 15.2438C10.9304 15.0875 10.7184 14.9997 10.4974 14.9997C10.2764 14.9997 10.0644 15.0875 9.90814 15.2438C9.75186 15.4 9.66406 15.612 9.66406 15.833Z"
                stroke="var(--text-grey-neutral)"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.66406 4.16634C9.66406 4.38735 9.75186 4.59932 9.90814 4.7556C10.0644 4.91188 10.2764 4.99967 10.4974 4.99967C10.7184 4.99967 10.9304 4.91188 11.0867 4.7556C11.2429 4.59932 11.3307 4.38735 11.3307 4.16634C11.3307 3.94533 11.2429 3.73337 11.0867 3.57709C10.9304 3.42081 10.7184 3.33301 10.4974 3.33301C10.2764 3.33301 10.0644 3.42081 9.90814 3.57709C9.75186 3.73337 9.66406 3.94533 9.66406 4.16634Z"
                stroke="var(--text-grey-neutral)"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )
      }
    ].filter(Boolean),
    [formatMicrosecondsToDate, formatFileSize, isMobile]
  );

  const parseSelectedDateRange = (selectedDate) => {
    if (!selectedDate) return { start: null, end: null };

    const [startDateStr, endDateStr] = selectedDate.split(" - ");

    if (!startDateStr || !endDateStr) {
      console.error("Invalid date range format:", selectedDate);
      return { start: null, end: null };
    }

    // Parse DD/MM/YYYY format
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.trim().split("/");
      // Month is 0-indexed in JavaScript Date
      return new Date(year, month - 1, day);
    };

    const startDate = parseDate(startDateStr);
    const endDate = parseDate(endDateStr);

    // Validate dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error("Invalid date values:", startDateStr, endDateStr);
      return { start: null, end: null };
    }

    return { start: startDate, end: endDate };
  };

  // Filter logs based on operation type and user (date filtering is done by API)
  const filteredLogs = logData.filter((log) => {
    const matchesOperation =
      !selectedSecretType || log.file_name?.toLowerCase().includes(selectedSecretType.toLowerCase());
    const matchesUser = !selectedUser || log.file_name?.toLowerCase().includes(selectedUser.toLowerCase());

    return matchesOperation && matchesUser;
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data: filteredLogs });

  const isButtonDisabled = !(
    (selectedSecretType || selectedUser || selectedDate) &&
    filteredLogs?.length > 0
  );

  const exportCSV = () => {
    const csvRows = [];
    const headers = ["Log File Name", "Start Time", "End Time", "File Size"];
    csvRows.push(headers.join(","));

    filteredLogs?.forEach((log) => {
      const row = [
        log.file_name,
        formatMicrosecondsToDate(log.start_time),
        formatMicrosecondsToDate(log.end_time),
        formatFileSize(log.file_size)
      ].join(",");
      csvRows.push(row);
    });

    const csvString = csvRows.join("\n");

    const blob = new Blob([csvString], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "logs.csv";
    link.click();
  };

  return (
    <>
      <section
        className="container logsContainer"
        style={{
          backgroundColor: theme === "light" ? "white" : "#1C191E"
        }}
      >
        <div className="logsHeaderContainer">
          <div className="logsHeader">
            {/* {organization && (
              <SelectDropDown
                label=""
                placeholder="Select Users"
                selectedCountry={selectedUser}
                data={users}
                onSelectCountry={(value) => setSelectedUser(value)}
                width={isMobile ? "90%" : "93%"}
              />
            )} */}

            {/* <SelectDropDown
              label=""
              placeholder="Select Operations"
              selectedCountry={selectedSecretType}
              data={secretTypeOptions}
              onSelectCountry={(value) => setSelectedSecretType(value)}
              width={isMobile ? "90%" : "93%"}
            /> */}

            <DatePicker
              setSelectedDate={setSelectedDate}
              selectedDate={selectedDate}
            />
          </div>


        </div>

        {true && (
          <div
            className="tableContainer"
            style={{
              backgroundColor: theme === "light" ? "white" : "#1C191E",
              paddingTop: "0px",
              overflowY: "auto",
              minHeight: "calc(100vh - 280px)",
              maxHeight: "calc(100vh - 280px)"
            }}
          >
            {loading ? (
              <div className="logs-loader">
                <div className="logs-spinner"></div>
                <div className="loading-text">Fetching logs...</div>
              </div>
            ) : filteredLogs?.length === 0 ? (
              <div className="secretNoDataFoundLogs">
                <img src={noData} alt="No Data Found" className="noDataImage" />
                <p className="noDataText">No Result found</p>
              </div>
            ) : (
              <table {...getTableProps()} className="logsTable">
                <thead className="tableHead">
                  {headerGroups.map((headerGroup) => {
                    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={key} {...headerGroupProps} className="tableHeadRow">
                        {headerGroup.headers.map((column) => {
                          const { key: columnKey, ...columnProps } = column.getHeaderProps();
                          return (
                            <th
                              key={columnKey}
                              {...columnProps}
                              className="tableHeadRowHeaderBox"
                              style={{ width: column.width }}
                            >
                              {column.render("Header")}
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="tableBody"
                  style={{
                    backgroundColor: theme === "light" ? "white" : "#1C191E",
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8
                  }}
                >
                  {rows.map((row) => {
                    prepareRow(row);
                    const { key: rowKey, ...rowProps } = row.getRowProps();
                    return (
                      <tr key={rowKey} {...rowProps} className="tableBodyRow">
                        {row.cells.map((cell) => {
                          const { key: cellKey, ...cellProps } = cell.getCellProps();
                          return (
                            <td
                              key={cellKey}
                              {...cellProps}
                              className="tableBodyRowData"
                              style={{ width: cell.column.width }}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {menu.selectedRow && menu.selectedOption === "menu" && createPortal(
        <div
          className="logsMenuPortal"
          style={{
            position: "fixed",
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
        >
          <MenuDropDown
            list={logMenuList}
            setMenu={setMenu}
            row={selectedRow}
            setSelectedRow={setSelectedRow}
            downloadFile={() => { }}
            restoreFromTrash={() => { }}
            DeleteFromTrash={() => { }}
            tableMode="table"
            subDropDown={subDropDown}
            setSubDropDown={setSubDropDown}
          />
        </div>,
        document.body
      )}

      {/* Log Details Modal */}
      {menu.selectedOption === "Details" && selectedRow && (
        <div className="logDetailsModal" style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div className="logDetailsContent" style={{
            backgroundColor: theme === "light" ? "#ffffff" : "#1C191E",
            padding: isMobile ? "16px" : "24px",
            borderRadius: "12px",
            maxWidth: isMobile ? "92%" : "500px",
            width: isMobile ? "92%" : "auto",
            minWidth: isMobile ? "auto" : "450px",
            maxHeight: "85vh",
            overflowY: "auto",
            color: theme === "light" ? "#000" : "#fff",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? "16px" : "24px" }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? "16px" : "18px", fontWeight: 600, color: theme === "light" ? "#1a1a1a" : "#fff" }}>Log File Details</h2>
              <button
                onClick={handleMenu}
                style={{
                  background: "transparent",
                  border: "1px solid var(--stroke-grey-medium)",
                  borderRadius: "6px",
                  width: isMobile ? "28px" : "32px",
                  height: isMobile ? "28px" : "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isMobile ? "18px" : "20px",
                  cursor: "pointer",
                  color: theme === "light" ? "#666" : "#999",
                  padding: 0
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {[
                { label: "File Name", value: selectedRow.file_name, breakAll: true },
                { label: "Start Time", value: formatMicrosecondsToDate(selectedRow.start_time) },
                { label: "End Time", value: formatMicrosecondsToDate(selectedRow.end_time) },
                { label: "File Size", value: formatFileSize(selectedRow.file_size) },
                { label: "Log Hash", value: selectedRow.log_hash, mono: true },
                { label: "Signed Hash", value: selectedRow.signed_hash, mono: true },
                { label: "Transaction ID", value: selectedRow.transaction_id || "txn_id_0", mono: true }
              ].map((item, index, array) => (
                <div 
                  key={item.label} 
                  style={{ 
                    display: "flex", 
                    flexDirection: isMobile ? "column" : "row", 
                    alignItems: isMobile ? "flex-start" : "center",
                    borderBottom: index === array.length - 1 ? "none" : `1px solid ${theme === "light" ? "#e5e5e5" : "#333"}`, 
                    paddingTop: isMobile ? "10px" : "14px", 
                    paddingBottom: isMobile ? "10px" : "14px",
                    gap: isMobile ? "4px" : "20px"
                  }}
                >
                  <div style={{ 
                    flex: isMobile ? "none" : "0 0 140px", 
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: isMobile ? "10px" : "11px", 
                    color: theme === "light" ? "#727272" : "#999", 
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    paddingRight: isMobile ? "0" : "10px"
                  }}>
                    <span>{item.label}</span>
                    <span>:</span>
                  </div>
                  <div style={{ 
                    flex: 1,
                    fontSize: isMobile ? "12px" : "13.5px", 
                    fontWeight: 500, 
                    wordBreak: "break-all", 
                    fontFamily: item.mono ? "monospace" : "inherit",
                    color: theme === "light" ? "#1a1a1a" : "#fff",
                    lineHeight: "1.4"
                  }}>
                    {item.value || "N/A"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* {isMobile && (
        <div className="mobileButton">
          <Button
            csv
            type="violet"
            width="87%"
            title="Export CSV"
            onClick={exportCSV}
            disabled={isButtonDisabled}
          />
        </div>
      )} */}
    </>
  );
};

export default Logs;